import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

interface CreditCustomer {
  id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  public_nickname: string | null;
}

type TxType =
  | 'review_reward'
  | 'review_image_reward'
  | 'review_revoke'
  | 'order_spend'
  | 'order_refund'
  | 'admin_grant'
  | 'admin_deduct'
  | string;

interface CreditTransaction {
  id: string;
  amount: number;
  type: TxType;
  referenceType: string | null;
  referenceId: string | number | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
}

interface CreditLedger {
  customer: CreditCustomer;
  balance: number;
  transactions: CreditTransaction[];
}

interface AdjustResponse {
  success: boolean;
  amount: number;
  balance: number;
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const TYPE_LABELS: Record<string, string> = {
  review_reward: '評論獎勵',
  review_image_reward: '附圖評論獎勵',
  review_revoke: '評論取消',
  order_spend: '訂單折抵',
  order_refund: '訂單退回',
  admin_grant: '管理員發放',
  admin_deduct: '管理員扣除',
};

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(
      d.getDate()
    ).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(
      2,
      '0'
    )}`;
  } catch {
    return iso;
  }
}

function formatRef(tx: CreditTransaction): string {
  if (!tx.referenceType && !tx.referenceId) return '—';
  const parts: string[] = [];
  if (tx.referenceType) parts.push(tx.referenceType);
  if (tx.referenceId !== null && tx.referenceId !== undefined) parts.push(`#${tx.referenceId}`);
  return parts.join(' ');
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

const CreditsAdminPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [ledger, setLedger] = useState<CreditLedger | null>(null);

  // Adjust dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // --------------------------------------------------------------------------
  // Lookup
  // --------------------------------------------------------------------------

  const fetchLedger = useCallback(
    async (customerId: number, silent = false) => {
      if (!silent) setLoading(true);
      try {
        const { data } = await api.get<CreditLedger>(`/api/credits/admin/${customerId}`);
        setLedger(data);
      } catch (err: any) {
        setLedger(null);
        enqueueSnackbar(err?.response?.data?.message || '找不到客戶或載入失敗', {
          variant: 'error',
        });
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    if (!trimmed) {
      enqueueSnackbar('請輸入客戶 ID', { variant: 'warning' });
      return;
    }
    const id = parseInt(trimmed, 10);
    if (!Number.isFinite(id) || id <= 0) {
      enqueueSnackbar('客戶 ID 必須是正整數', { variant: 'warning' });
      return;
    }
    fetchLedger(id);
  };

  const handleSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  // --------------------------------------------------------------------------
  // Adjust dialog
  // --------------------------------------------------------------------------

  const openAdjustDialog = () => {
    setAmountInput('');
    setNoteInput('');
    setDialogOpen(true);
  };

  const closeAdjustDialog = () => {
    if (submitting) return;
    setDialogOpen(false);
  };

  const parsedAmount = parseInt(amountInput, 10);
  const amountValid =
    Number.isFinite(parsedAmount) &&
    parsedAmount !== 0 &&
    parsedAmount >= -999999 &&
    parsedAmount <= 999999;
  const isDeduction = Number.isFinite(parsedAmount) && parsedAmount < 0;
  const noteValid = !isDeduction || noteInput.trim().length > 0;

  const submitAdjust = async () => {
    if (!ledger) return;
    if (!amountValid) {
      enqueueSnackbar('金額必須是 -999999 到 999999 之間的非零整數', { variant: 'warning' });
      return;
    }
    if (!noteValid) {
      enqueueSnackbar('扣除購物金時必須填寫備註', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post<AdjustResponse>(
        `/api/credits/admin/${ledger.customer.id}/adjust`,
        {
          amount: parsedAmount,
          note: noteInput.trim() || undefined,
        }
      );
      const verb = data.amount > 0 ? '發放' : '扣除';
      enqueueSnackbar(`已${verb} NT$${Math.abs(data.amount)}，新餘額 NT$${data.balance}`, {
        variant: 'success',
      });
      setDialogOpen(false);
      // Refresh ledger
      fetchLedger(ledger.customer.id, true);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || '調整購物金失敗', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        購物金管理
      </Typography>

      {/* Search bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="客戶 ID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKey}
            size="small"
            placeholder="輸入客戶 ID（整數）"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 320 }}
          />
          <Button variant="contained" onClick={handleSearch} disabled={loading}>
            {loading ? '查詢中…' : '查詢'}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          提示：目前僅支援以客戶 ID 查詢。
        </Typography>
      </Paper>

      {loading && !ledger && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {ledger && (
        <>
          {/* Customer + balance card */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h6">
                    {ledger.customer.name || '（未命名）'}
                    {ledger.customer.public_nickname ? ` (${ledger.customer.public_nickname})` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    客戶 ID: {ledger.customer.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Email: {ledger.customer.email || '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    電話: {ledger.customer.phone || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      textAlign: { xs: 'left', md: 'right' },
                      borderLeft: { md: '1px solid' },
                      borderColor: { md: 'divider' },
                      pl: { md: 2 },
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      目前餘額
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      NT${ledger.balance}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<WalletIcon />}
                      onClick={openAdjustDialog}
                      sx={{ mt: 1 }}
                    >
                      手動調整購物金
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Transaction table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1">交易紀錄（最近 200 筆）</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 150 }}>時間</TableCell>
                    <TableCell sx={{ width: 120 }} align="right">
                      金額
                    </TableCell>
                    <TableCell sx={{ width: 140 }}>類型</TableCell>
                    <TableCell>備註</TableCell>
                    <TableCell sx={{ width: 160 }}>操作者</TableCell>
                    <TableCell sx={{ width: 150 }}>關聯</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledger.transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        尚無交易紀錄
                      </TableCell>
                    </TableRow>
                  ) : (
                    ledger.transactions.map((tx) => {
                      const neg = tx.amount < 0;
                      const label = TYPE_LABELS[tx.type] || tx.type;
                      return (
                        <TableRow key={tx.id} hover>
                          <TableCell>{formatDate(tx.createdAt)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              color: neg ? 'error.main' : 'success.main',
                            }}
                          >
                            {neg ? '' : '+'}
                            {tx.amount}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={label} variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{tx.note || '—'}</TableCell>
                          <TableCell>{tx.createdBy || '—'}</TableCell>
                          <TableCell>{formatRef(tx)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Adjust dialog */}
      <Dialog open={dialogOpen} onClose={closeAdjustDialog} maxWidth="sm" fullWidth>
        <DialogTitle>手動調整購物金</DialogTitle>
        <DialogContent>
          {ledger && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              客戶：{ledger.customer.name} (#{ledger.customer.id}) · 目前餘額 NT$
              {ledger.balance}
            </Typography>
          )}
          <TextField
            label="金額（正數=發放，負數=扣除）"
            type="number"
            value={amountInput}
            onChange={(e) => setAmountInput(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            inputProps={{ min: -999999, max: 999999, step: 1 }}
            helperText="輸入範圍 -999999 ~ 999999，不可為 0。"
            error={amountInput.length > 0 && !amountValid}
          />
          <TextField
            label={isDeduction ? '備註（扣除時必填，最多 500 字）' : '備註（選填，最多 500 字）'}
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value.slice(0, 500))}
            multiline
            rows={3}
            fullWidth
            size="small"
            required={isDeduction}
            error={isDeduction && noteInput.trim().length === 0}
          />
          {isDeduction && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              你即將扣除客戶購物金，請於備註寫明原因（例如：客服確認訂單取消後回收獎勵）。
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdjustDialog} disabled={submitting}>
            取消
          </Button>
          <Button
            variant="contained"
            color={isDeduction ? 'warning' : 'primary'}
            onClick={submitAdjust}
            disabled={submitting || !amountValid || !noteValid}
          >
            {submitting ? '處理中…' : '確認調整'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreditsAdminPage;
